import { ReactElement, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Step, StepStatus } from '@/components/ui/custom/Steps/Steps';
import { updateCoursePath } from '@/data/services/coursePathServices';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { Course } from '@/models/Course';
import { updateCourse } from '@/data/services/courseServices';
import { OmitCourseAndObjectives } from '../CourseStudio.hooks';

export type StudioStep = 'info' | 'objectives' | 'matrix' | 'details';

export type CourseStudioProps = {
  disabled?: boolean;
  loading?: boolean;
};

export type StepInformation = {
  title: string;
  component: ReactNode;
  actions?: ReactElement[];
};

export const studioStepsIndex: Record<StudioStep, number> = {
  info: 0,
  objectives: 1,
  matrix: 2,
  details: 3,
};

export const studioStepsPaths: Record<number, StudioStep> = {
  0: 'info',
  1: 'objectives',
  2: 'matrix',
  3: 'details',
};

type CourseStudioStepsOptions = {
  count: number;
  onClick?: (step: number) => void;
};

export const useCourseStudioSteps = (options: CourseStudioStepsOptions) => {
  const { studioStep } = useParams();

  const { count, onClick } = options;

  const [step, setStep] = useState(
    studioStepsIndex[studioStep as StudioStep] ?? 0,
  );

  const stepStatus = (index: number): StepStatus => {
    if (index < step) return 'done';
    if (index === step) return 'active';
    return 'todo';
  };

  const steps: Step[] = Array.from({ length: count }, (_, index) => {
    return {
      id: index,
      status: stepStatus(index),
      onClick,
    };
  });

  return {
    step,
    setStep,
    steps,
    studioStepsPaths,
  };
};

export function useCourseStudio() {
  async function handleUpdateCourse(currentData: Course, newData: Course) {
    let updated: Course = { ...currentData };

    const courseChanges = buildChangedObject<Course>(currentData, newData);

    if (Object.keys(courseChanges).length) {
      const updatedCourse = await updateCourse(currentData.id, courseChanges);

      updated = { ...updatedCourse };
    }

    return updated;
  }

  async function handleUpdateCoursePath(
    currentData: OmitCourseAndObjectives,
    newData: OmitCourseAndObjectives,
  ) {
    let updated: OmitCourseAndObjectives = { ...currentData };

    const changes = buildChangedObject<OmitCourseAndObjectives>(
      currentData,
      newData,
    );

    if (Object.keys(changes).length) {
      const updatedCoursePath = await updateCoursePath(
        currentData.slug,
        changes,
      );

      updated = { ...updatedCoursePath };
    }

    return updated;
  }

  return {
    handleUpdateCourse,
    handleUpdateCoursePath,
  };
}

export function separeArrayStudioActions<ArrayType extends { id?: number }>(
  currentArray: ArrayType[],
  newArray: ArrayType[],
) {
  const arrayToDelete = currentArray.filter(
    model => !newArray.some(model2 => model2.id === model.id),
  );

  const arrayToUpdate = newArray.filter(
    model => model.id && currentArray.some(model2 => model2.id === model.id),
  );

  const arrayToCreate = newArray.filter(model => !model.id);

  return {
    arrayToDelete,
    arrayToUpdate,
    arrayToCreate,
  };
}
